import React from 'react';
import Navbar from '@/components/Navbar';
import { Card } from '@/components/ui/card';

type Row = string[];

function DataTable({ head, rows }: { head: Row; rows: Row[] }) {
  return (
    <div className="overflow-x-auto -mx-2 px-2">
      <table className="w-full min-w-[520px] text-sm border-collapse">
        <thead>
          <tr>
            {head.map((cell) => (
              <th
                key={cell}
                className="bg-brand-azul text-white font-display font-semibold text-left px-3 py-2 align-top"
              >
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i % 2 === 1 ? 'bg-muted/40' : undefined}>
              {row.map((cell, j) => (
                <td key={j} className="border-b border-border px-3 py-2 align-top text-brand-azul/80">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PoliticaDePrivacidade() {
  const updatedAt = '2026-08-11';
  const versao = '1.0';

  return (
    <div className="min-h-screen bg-muted/30">
      <Navbar />

      {/* Main Content */}
      <main className="container px-4 mx-auto py-12">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <a href="/" className="text-brand-azul hover:text-brand-roxo transition-colors underline">Voltar</a>
          </div>

          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-brand-azul mb-2">Aviso de Privacidade</h1>
          <p className="text-brand-azul/60 mb-6">Versão {versao} — última atualização: {updatedAt}</p>

          <Card className="p-8 space-y-6 border-border rounded-2xl">
            <p className="border-l-4 border-brand-roxo bg-brand-roxo/5 text-brand-azul/80 text-sm px-4 py-3 rounded-r-lg">
              Este Aviso descreve como a DGenny Tecnologia LTDA trata dados pessoais na plataforma dgenny® Compre.AI,
              no portal de adesão e nos sites institucionais. Complementa — e não substitui — as cláusulas de proteção
              de dados do contrato de licenciamento e o Acordo de Tratamento de Dados Pessoais (DPA) firmado com cada
              cliente. Substitui e consolida a Política de Privacidade anteriormente publicada.
            </p>

            <section className="space-y-2">
              <h2 className="text-xl font-display font-bold text-brand-azul">1. Quem somos e o que este Aviso cobre</h2>
              <p className="text-brand-azul/80">
                DGENNY TECNOLOGIA LTDA, inscrita no CNPJ/MF sob o nº 60.232.645/0001-81, com sede na Rua Frei Caneca,
                520, Agronômica, Florianópolis/SC, CEP 88.025-053.
              </p>
              <p className="text-brand-azul/80">
                A dgenny® Compre.AI é uma plataforma de automação de suprimentos: Assistentes de IA cotam e negociam
                com fornecedores pelo WhatsApp e por e-mail, organizam propostas, equalizam preços e registram o
                histórico da operação de compras do cliente.
              </p>
              <p className="text-brand-azul/80">Este Aviso se aplica a:</p>
              <ul className="list-disc pl-5 text-brand-azul/80 space-y-1">
                <li>a plataforma dgenny® Compre.AI, utilizada pelos clientes contratantes e seus usuários;</li>
                <li>o portal de adesão e contratação, incluindo cadastro, aceite de termos, faturamento e cobrança;</li>
                <li>os sites institucionais da dgenny e os canais de contato comercial.</li>
              </ul>
            </section>

            <section className="space-y-2">
              <h2 className="text-xl font-display font-bold text-brand-azul">2. Papéis no tratamento</h2>
              <p className="text-brand-azul/80">
                <strong>O cliente contratante é o Controlador</strong> dos dados pessoais tratados na plataforma — dele
                são as decisões sobre quais dados inserir, para quais finalidades e por quanto tempo. A dgenny atua como{' '}
                <strong>Operadora</strong>, tratando esses dados exclusivamente em nome do cliente e conforme instruções
                documentadas.
              </p>
              <p className="text-brand-azul/80">
                A dgenny atua como <strong>Controladora</strong> em relação aos dados de cadastro, adesão, faturamento e
                relacionamento comercial dos seus próprios clientes e interessados, aos registros de aceite de termos e
                aos registros de acesso aos seus sites e ao portal de adesão.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-display font-bold text-brand-azul">3. Dados pessoais tratados</h2>
              <p className="text-brand-azul/80 font-medium">
                3.1. Na plataforma, em nome do cliente contratante (dgenny como Operadora):
              </p>
              <DataTable
                head={['Categoria de titular', 'Dados tratados']}
                rows={[
                  ['Usuários do cliente', 'Nome, e-mail corporativo, telefone, cargo, identificador de acesso, registros de uso'],
                  ['Contatos de fornecedores', 'Nome, telefone/WhatsApp, e-mail, empresa, cargo, conteúdo das mensagens de cotação e negociação'],
                  ['Contatos operacionais', 'Nome, e-mail, telefone, unidade, obra ou centro de custo'],
                ]}
              />
              <p className="text-brand-azul/80 font-medium">
                3.2. No portal de adesão e nos sites institucionais (dgenny como Controladora):
              </p>
              <DataTable
                head={['Categoria', 'Dados tratados']}
                rows={[
                  ['Identificação e contato', 'Nome, CPF/CNPJ, e-mail, telefone, cargo'],
                  ['Endereço', 'CEP, logradouro, número, complemento, bairro, cidade e estado'],
                  ['Faturamento e cobrança', 'Dados necessários à emissão de nota fiscal e à cobrança, processados por parceiros de pagamento conforme as políticas destes'],
                  ['Registros de aceite', 'Data e hora do aceite de termos e deste Aviso, endereço IP e identificação do navegador'],
                  ['Dados de navegação', 'Páginas acessadas, data e hora, endereço IP, identificadores de dispositivo e navegador'],
                ]}
              />
              <p className="text-brand-azul/80">
                Não tratamos dados pessoais sensíveis nem dados de crianças e adolescentes. Não realizamos
                enriquecimento de bases de terceiros e não utilizamos dados da operação de compras dos clientes para
                publicidade.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-display font-bold text-brand-azul">4. Finalidades e bases legais</h2>
              <DataTable
                head={['Finalidade', 'Base legal (LGPD)']}
                rows={[
                  ['Executar o contrato: operar a plataforma, conduzir cotações e negociações, processar propostas e gerar relatórios', 'Execução de contrato (art. 7º, V)'],
                  ['Comunicar-se com fornecedores em nome do cliente, pelo canal já utilizado no relacionamento comercial', 'Legítimo interesse do Controlador (art. 7º, IX)'],
                  ['Prestar suporte técnico e resolver incidentes', 'Execução de contrato (art. 7º, V)'],
                  ['Garantir segurança da informação, rastreabilidade e prevenção a fraudes', 'Legítimo interesse (art. 7º, IX)'],
                  ['Cadastrar, provisionar e habilitar o acesso ao sistema no portal de adesão', 'Execução de contrato ou diligências preliminares (art. 7º, V)'],
                  ['Emitir notas fiscais, cobranças e cumprir obrigações fiscais e regulatórias', 'Cumprimento de obrigação legal (art. 7º, II)'],
                  ['Registrar o aceite de termos e deste Aviso, com IP e data/hora', 'Cumprimento de obrigação legal e legítimo interesse (arts. 7º, II e IX)'],
                  ['Manter registros de acesso a aplicações de internet', 'Cumprimento de obrigação legal (Marco Civil da Internet, art. 15)'],
                  ['Enviar comunicações comerciais e de marketing', 'Consentimento (art. 7º, I), revogável a qualquer momento'],
                  ['Melhorar o produto a partir de dados anonimizados ou agregados', 'Dados anonimizados — fora do escopo da LGPD (art. 12)'],
                ]}
              />
            </section>

            <section className="space-y-2">
              <h2 className="text-xl font-display font-bold text-brand-azul">5. Assistentes de IA e canal WhatsApp</h2>
              <p className="text-brand-azul/80">
                As cotações e negociações são conduzidas por Assistentes de IA que se comunicam com fornecedores pelo
                WhatsApp Business Platform e por e-mail. Todas as interações ficam registradas na plataforma, formando a
                trilha de auditoria da negociação.
              </p>
              <p className="text-brand-azul/80">
                Os resultados produzidos pelos Assistentes têm natureza probabilística e destinam-se a apoiar a decisão
                humana. A decisão final de compra é sempre do cliente contratante.
              </p>
              <p className="text-brand-azul/80">
                Cabe ao cliente, como Controlador, informar seus fornecedores, quando aplicável, sobre a utilização de
                assistentes virtuais na comunicação e sobre o registro das interações.
              </p>
              <p className="text-brand-azul/80">
                As mensagens trafegam pela infraestrutura da Meta Platforms, sujeitando-se também às políticas de
                privacidade e segurança daquela plataforma.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-display font-bold text-brand-azul">6. Compartilhamento e suboperadores</h2>
              <p className="text-brand-azul/80">
                Não vendemos dados pessoais e não os compartilhamos para finalidades publicitárias. O compartilhamento
                ocorre apenas com os prestadores necessários à operação do serviço:
              </p>
              <DataTable
                head={['Suboperador', 'Serviço prestado', 'Local de processamento']}
                rows={[
                  ['Google Cloud Platform / Firebase', 'Hospedagem, banco de dados, autenticação e armazenamento', 'Estados Unidos / Brasil'],
                  ['Meta Platforms (WhatsApp Business Platform)', 'Canal de mensageria com fornecedores', 'Estados Unidos'],
                  ['Provedores de modelos de IA (ex.: OpenAI, Anthropic, Google)', 'Processamento de linguagem natural dos Assistentes', 'Estados Unidos'],
                  ['Provedores de e-mail transacional', 'Envio de notificações e comunicações operacionais', 'Estados Unidos / Brasil'],
                  ['Plataforma de assinatura eletrônica', 'Formalização de documentos', 'Brasil'],
                  ['Meios de pagamento (ex.: Asaas) e emissão fiscal', 'Cobrança, faturamento e obrigações fiscais', 'Brasil'],
                ]}
              />
              <p className="text-brand-azul/80">
                Também compartilhamos dados com autoridades quando exigido por lei, decisão judicial ou ordem
                regulatória. Alterações relevantes na relação de suboperadores são comunicadas previamente ao cliente,
                que pode apresentar objeção fundamentada nos termos do contrato.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-xl font-display font-bold text-brand-azul">7. Transferência internacional</h2>
              <p className="text-brand-azul/80">
                Parte do processamento ocorre fora do Brasil, em razão dos provedores acima. Essas transferências são
                realizadas apenas quando amparadas nas hipóteses do art. 33 da LGPD e mediante garantias contratuais de
                nível adequado de proteção, incluindo as cláusulas contratuais padrão dos respectivos provedores.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-xl font-display font-bold text-brand-azul">8. Retenção e eliminação</h2>
              <p className="text-brand-azul/80">
                Durante a vigência do contrato, os dados permanecem disponíveis na plataforma e podem ser exportados
                pelo cliente a qualquer momento.
              </p>
              <p className="text-brand-azul/80">
                Encerrado o contrato, o cliente dispõe de <strong>30 (trinta) dias corridos</strong> para solicitar a
                devolução dos dados em formato estruturado e de uso comum. Decorrido esse prazo, os dados são eliminados
                dos ambientes produtivos em até <strong>60 (sessenta) dias</strong>, e das rotinas de backup conforme o
                ciclo natural de expiração destas. Declaração de eliminação é fornecida mediante solicitação.
              </p>
              <p className="text-brand-azul/80">
                Permanecem retidos apenas: dados cuja guarda seja exigida por obrigação legal ou regulatória; dados
                necessários ao exercício regular de direitos em processo; registros de acesso mantidos para fins de
                segurança e rastreabilidade; e dados anonimizados, que não permitem identificar titulares.
              </p>
              <p className="text-brand-azul/80">
                Os dados de cadastro, contratação, faturamento e registros de aceite tratados pela dgenny como
                Controladora são mantidos durante a relação comercial e, após seu término, pelos prazos legais
                aplicáveis — em especial os prazos fiscais e prescricionais — sendo depois eliminados ou anonimizados.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-xl font-display font-bold text-brand-azul">9. Cookies e tecnologias similares</h2>
              <p className="text-brand-azul/80">
                Nossos sites e o portal de adesão podem utilizar cookies e tecnologias similares para viabilizar o
                funcionamento das páginas, lembrar preferências, medir audiência e aprimorar a experiência de navegação.
                Você pode gerenciar ou bloquear cookies nas configurações do navegador, ciente de que alguns recursos
                podem deixar de funcionar corretamente. Cookies não essenciais dependem de consentimento, que pode ser
                revogado a qualquer momento.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-xl font-display font-bold text-brand-azul">10. Segurança da informação</h2>
              <p className="text-brand-azul/80">
                Adotamos medidas técnicas e organizacionais compatíveis com a natureza do serviço, entre elas: tráfego
                criptografado (HTTPS/TLS), criptografia dos dados em repouso pela infraestrutura de nuvem, controle de
                acesso por perfis segundo o princípio do menor privilégio, autenticação multifator nos acessos
                administrativos, segregação lógica dos dados de cada cliente, rotinas de backup, registro de acessos e
                operações relevantes, e obrigação de confidencialidade de toda a equipe.
              </p>
              <p className="text-brand-azul/80">
                Nenhum ambiente é absolutamente imune a incidentes. Por isso mantemos processo de resposta e melhoria
                contínua das medidas adotadas, sem redução do nível de proteção.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-xl font-display font-bold text-brand-azul">11. Incidentes de segurança</h2>
              <p className="text-brand-azul/80">
                Verificado incidente que possa acarretar risco ou dano relevante aos titulares, comunicamos o cliente
                contratante em até <strong>24 (vinte e quatro) horas</strong> da ciência, informando a natureza do
                evento, os dados e titulares potencialmente afetados, as consequências prováveis e as medidas de
                contenção adotadas. Cooperamos com o cliente nas comunicações à ANPD e aos titulares, cabendo a ele,
                como Controlador, a decisão sobre tais comunicações.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-xl font-display font-bold text-brand-azul">12. Direitos dos titulares</h2>
              <p className="text-brand-azul/80">
                Nos termos do art. 18 da LGPD, o titular pode solicitar: confirmação da existência de tratamento; acesso
                aos dados; correção de dados incompletos, inexatos ou desatualizados; anonimização, bloqueio ou
                eliminação de dados desnecessários ou tratados em desconformidade; portabilidade; informação sobre
                compartilhamentos; e revogação do consentimento, quando esta for a base legal aplicável.
              </p>
              <p className="text-brand-azul/80">
                Quanto aos dados tratados <strong>na plataforma</strong>, a dgenny atua como Operadora: as solicitações
                são encaminhadas ao cliente contratante (Controlador), a quem cabe decidir sobre o atendimento, com o
                apoio técnico da dgenny em até 5 (cinco) dias úteis.
              </p>
              <p className="text-brand-azul/80">
                Quanto aos dados de <strong>cadastro, adesão, faturamento e navegação</strong>, em que a dgenny atua
                como Controladora, atendemos diretamente as solicitações dentro dos prazos legais, mediante validação de
                identidade quando necessário.
              </p>
              <p className="text-brand-azul/80">
                Para exercer seus direitos ou esclarecer dúvidas sobre este Aviso, escreva para{' '}
                <a href="mailto:privacidade@dgenny.com.br" className="underline hover:text-brand-azul">privacidade@dgenny.com.br</a>.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-xl font-display font-bold text-brand-azul">
                13. Encarregado pelo Tratamento de Dados Pessoais (DPO)
              </h2>
              <p className="text-brand-azul/80">
                DGENNY TECNOLOGIA LTDA — CNPJ 60.232.645/0001-81
                <br /> Endereço: Rua Frei Caneca, 520, G41PAVGA, Florianópolis/SC, CEP 88.025-053
                <br /> Contato do Encarregado:{' '}
                <a href="mailto:privacidade@dgenny.com.br" className="underline hover:text-brand-azul">privacidade@dgenny.com.br</a>
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-xl font-display font-bold text-brand-azul">14. Atualizações deste Aviso</h2>
              <p className="text-brand-azul/80">
                Este Aviso pode ser atualizado para refletir mudanças na plataforma, na legislação ou nas práticas de
                tratamento. A versão vigente é sempre a publicada nesta página, identificada pela data de atualização
                acima. Alterações relevantes são comunicadas aos clientes pelos canais contratuais.
              </p>
            </section>
          </Card>

          <div className="text-center text-sm text-brand-azul/60 mt-6">
            <p>
              Consulte também nossos{' '}
              <a href="/termos-de-uso" className="underline hover:text-brand-azul">Termos de Uso</a>.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
